import { Event, EventSourcePolyfill } from 'event-source-polyfill'
import { useCallback, useEffect, useRef, useState } from 'react'
import { ChatStreamRequest, getChatStreamerEvent } from '../../api/modules/chatApi'
import { useAuth } from '../../context/AuthContext'
import { Agent } from '../../types/agentTypes'
import { KnowledgeSource, Reference, Disclaimer } from '../../types/chatTypes'

interface ErrorEvent extends Event {
  type: 'error'
  status: number
  statusText: string
  error?: undefined
}

interface MessageEvent {
  message: string
  response_source?: 'internal_knowledge' | 'agent_llm'
}

interface MessageInfoEvent {
  message_id: string
  is_sensitive: boolean
  knowledge_source: KnowledgeSource
  references?: Reference[]
  response_source?: 'internal_knowledge' | 'agent_llm'
}

interface DisclaimerEvent {
  disclaimers: Disclaimer[]
}
interface ResponseTransitionEvent {
  response_source: 'internal_knowledge' | 'agent_llm'
  phase: 'internal_streaming' | 'llm_resumed'
}

interface DualResponse {
  internal_knowledge: string
  agent_llm: string
}

interface ServerErrorEvent {
  error_code?: string
  error_message?: string
  details?: {
    violations?: string[]
    validation_type?: string
    raw_error?: string
  }
}

export function useChatStream(
  chatStreamRequest: Omit<ChatStreamRequest, 'message'>,
  onMessageComplete: (
    message: string,
    tempId: string,
    messageId: string,
    isSensitive: boolean,
    knowledgeSource?: KnowledgeSource,
    references?: Reference[],
  ) => void,
  onStreamingUpdate: (
    partialMessage: string,
    tempId: string,
    messageId?: string,
    error?: Error,
    knowledgeSource?: KnowledgeSource,
    errorDetails?: { policy_violations?: string[] },
    responseSource?: 'internal_knowledge' | 'agent_llm',
    isSensitive?: boolean,
  ) => void,
  onResponseTransition?: (
    tempId: string,
    responseSource: 'internal_knowledge' | 'agent_llm',
    phase: 'internal_streaming' | 'llm_resumed',
  ) => void,
  isSensitive?: boolean,
  onDisclaimersReceived?: (disclaimers: Disclaimer[]) => void,
) {
  const [streamingAnswer, setStreamingAnswer] = useState('')
  const [dualResponse, setDualResponse] = useState<DualResponse>({ internal_knowledge: '', agent_llm: '' })
  const [currentResponseSource, setCurrentResponseSource] = useState<'internal_knowledge' | 'agent_llm' | null>(null)
  const [isSensitiveAnswer, setIsSensitiveAnswer] = useState(false)
  const [newMessageId, setNewMessageId] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const { refreshUser } = useAuth()
  const [knowledgeSource, setKnowledgeSource] = useState<KnowledgeSource>('LLM_ONLY')
  const [references, setReferences] = useState<Reference[]>([])

  const streamingAnswerRef = useRef('')
  const dualResponseRef = useRef<DualResponse>({ internal_knowledge: '', agent_llm: '' })
  const currentResponseSourceRef = useRef<'internal_knowledge' | 'agent_llm' | null>(null)
  const isSensitiveAnswerRef = useRef(false)
  const knowledgeSourceRef = useRef<KnowledgeSource>('LLM_ONLY')
  const newMessageIdRef = useRef('')
  const referencesRef = useRef<Reference[]>([])
  const sourceRef = useRef<EventSourcePolyfill | null>(null)

  const setupEventSource = useCallback(
    async (
      requestPayload: ChatStreamRequest,
      tempId: string,
      isSecondary = false,
    ): Promise<EventSourcePolyfill | null> => {
      try {
        const source = await getChatStreamerEvent(requestPayload)
        if (!source) {
          throw new Error('Could not establish connection. Please try again.')
        }
        sourceRef.current = source

        source.onopen = () => {
          setIsLoading(false)
        }

        source.onmessage = event => {
          const payload = JSON.parse(event.data) as MessageEvent

          // Route content based on response_source
          if (payload.response_source) {
            currentResponseSourceRef.current = payload.response_source
            setCurrentResponseSource(payload.response_source)

            dualResponseRef.current[payload.response_source] += payload.message
            setDualResponse({ ...dualResponseRef.current })

            // Pass the specific response content, not combined
            const responseContent = dualResponseRef.current[payload.response_source]
            onStreamingUpdate(
              responseContent,
              tempId,
              undefined,
              undefined,
              undefined,
              undefined,
              payload.response_source,
              undefined,
            )
          } else {
            // Fallback for single responses
            streamingAnswerRef.current += payload.message
            setStreamingAnswer(streamingAnswerRef.current)
            onStreamingUpdate(
              streamingAnswerRef.current,
              tempId,
              undefined,
              undefined,
              undefined,
              undefined,
              undefined,
              undefined,
            )
          }
        }

        source.addEventListener('message_info', (event: any) => {
          const payload = JSON.parse(event.data) as MessageInfoEvent

          if (payload.references) {
            referencesRef.current = payload.references
            setReferences(payload.references)
          }

          newMessageIdRef.current = payload.message_id
          setNewMessageId(payload.message_id)
          isSensitiveAnswerRef.current = payload.is_sensitive
          setIsSensitiveAnswer(payload.is_sensitive)
          knowledgeSourceRef.current = payload.knowledge_source
          setKnowledgeSource(payload.knowledge_source)

          // Pass the appropriate content based on response source
          const contentToUpdate = payload.response_source
            ? dualResponseRef.current[payload.response_source]
            : streamingAnswerRef.current

          onStreamingUpdate(
            contentToUpdate,
            tempId,
            isSecondary ? undefined : payload.message_id,
            undefined,
            isSecondary ? undefined : payload.knowledge_source,
            undefined,
            payload.response_source,
            payload.is_sensitive,
          )
        })

        source.addEventListener('response_transition', (event: any) => {
          const payload = JSON.parse(event.data) as ResponseTransitionEvent
          currentResponseSourceRef.current = payload.response_source
          setCurrentResponseSource(payload.response_source)

          // Notify parent component about the transition
          if (onResponseTransition) {
            onResponseTransition(tempId, payload.response_source, payload.phase)
          }
        })

        source.addEventListener('server-error', (event: any) => {
          const payload = JSON.parse(event.data) as ServerErrorEvent
          const errorCode = payload.error_code || 'server_error'
          const errorDetails = payload.details ? { policy_violations: payload.details.violations } : undefined

          let errorToSet: Error
          let messageToPreserve = ''
          const errorDetailsForAnswer: { policy_violations?: string[] } | undefined = errorDetails

          if (errorCode === 'USER_INPUT_CONTENT_VIOLATION') {
            errorToSet = new Error(errorCode)
          } else if (errorCode === 'BOT_RESPONSE_CONTENT_VIOLATION') {
            messageToPreserve = streamingAnswerRef.current
            errorToSet = new Error(errorCode)
          } else {
            // Handle all other server errors
            errorToSet = new Error(errorCode)
          }

          setError(errorToSet)
          // Pass the real message ID if available, so the error can be applied to the correct answer
          onStreamingUpdate(
            messageToPreserve,
            tempId,
            newMessageIdRef.current,
            errorToSet,
            undefined,
            errorDetailsForAnswer,
            undefined,
            undefined,
          )
          setIsLoading(false)

          source.close()
          sourceRef.current = null
        })

        source.addEventListener('disclaimers', (event: any) => {
          const payload = JSON.parse(event.data) as DisclaimerEvent
          if (payload.disclaimers && onDisclaimersReceived) {
            onDisclaimersReceived(payload.disclaimers)
          }
        })

        source.onerror = error => {
          const errorEvent = error as ErrorEvent
          if (errorEvent && errorEvent.status) {
            let errorMessage = 'An unknown error occurred'
            if (errorEvent.status === 401) {
              refreshUser && refreshUser()
              errorMessage = `${errorEvent.statusText}. Probeer opnieuw`
            } else {
              errorMessage = `${errorEvent.statusText}. Contacteer support`
            }
            const backendError = new Error(errorMessage)
            setError(backendError)
            onStreamingUpdate('', tempId, undefined, backendError, undefined, undefined, undefined, undefined)
          } else if (errorEvent?.error) {
            const backendError = new Error(errorEvent?.error)
            setError(backendError)
            onStreamingUpdate('', tempId, undefined, backendError, undefined, undefined, undefined, undefined)
          }
          setIsLoading(false)

          // For dual responses, complete each response separately
          if (
            dualResponseRef.current.internal_knowledge.trim() !== '' ||
            dualResponseRef.current.agent_llm.trim() !== ''
          ) {
            // Complete internal knowledge response if it exists
            if (dualResponseRef.current.internal_knowledge.trim() !== '') {
              onMessageComplete(
                dualResponseRef.current.internal_knowledge,
                tempId,
                newMessageIdRef.current,
                isSensitiveAnswerRef.current,
                'INTERNAL_KB',
                referencesRef.current,
              )
            }

            // Complete agent LLM response if it exists
            if (dualResponseRef.current.agent_llm.trim() !== '') {
              onMessageComplete(
                dualResponseRef.current.agent_llm,
                tempId,
                newMessageIdRef.current,
                isSensitiveAnswerRef.current,
                knowledgeSourceRef.current || 'LLM_ONLY',
                referencesRef.current,
              )
            }
          } else if (streamingAnswerRef.current.trim() !== '') {
            // Fallback for single responses
            onMessageComplete(
              streamingAnswerRef.current,
              tempId,
              newMessageIdRef.current,
              isSensitiveAnswerRef.current,
              knowledgeSourceRef.current,
              referencesRef.current,
            )
          }

          setStreamingAnswer('')
          source.close()
        }

        return source
      } catch (error) {
        setError(error as Error)
        setIsLoading(false)
        onStreamingUpdate('', tempId, undefined, error as Error, undefined, undefined, undefined, undefined)
        return null
      }
    },
    [refreshUser, onMessageComplete, onStreamingUpdate, onResponseTransition, onDisclaimersReceived],
  )

  const sendMessage = useCallback(
    async (
      message: string,
      tempId: string,
      agent?: Agent,
      assetId?: string,
      addiontalPayload?: Partial<ChatStreamRequest>,
    ) => {
      try {
        setIsLoading(true)
        setError(null)
        setStreamingAnswer('')
        streamingAnswerRef.current = ''
        setReferences([])
        referencesRef.current = []

        // Reset dual response objects
        const initialDualResponse = { internal_knowledge: '', agent_llm: '' }
        setDualResponse(initialDualResponse)
        dualResponseRef.current = initialDualResponse
        setCurrentResponseSource(null)
        currentResponseSourceRef.current = null

        // Close any existing streams
        if (sourceRef.current) {
          sourceRef.current.close()
          sourceRef.current = null
        }

        const requestPayload: ChatStreamRequest = {
          ...chatStreamRequest,
          ...addiontalPayload,
          message,
        }

        // Agent can be absent for existing chats
        if (agent) {
          requestPayload.agentId = agent.id
        }

        // Add fileIds to the request payload if documentId is provided
        if (assetId) {
          requestPayload.fileIds = [assetId]
        }

        // Remove different handling based on selectedTool; always create a single stream per call
        const source = await setupEventSource(requestPayload, tempId)
        if (source) {
          sourceRef.current = source
        } else {
          throw new Error('Could not establish connection. Please try again.')
        }
      } catch (error) {
        setError(error as Error)
        setIsLoading(false)
        onStreamingUpdate('', tempId, undefined, error as Error, undefined, undefined, undefined, undefined)

        // Close any streams that might have been opened
        if (sourceRef.current) {
          sourceRef.current.close()
          sourceRef.current = null
        }
      }
    },
    [chatStreamRequest, setupEventSource, onStreamingUpdate],
  )

  useEffect(() => {
    return () => {
      if (sourceRef.current) {
        sourceRef.current.close()
        sourceRef.current = null
      }
    }
  }, [])

  return {
    streamingAnswer,
    dualResponse,
    currentResponseSource,
    isSensitiveAnswer,
    newMessageId,
    isLoading,
    error,
    knowledgeSource,
    references,
    sendMessage,
  }
}
